/**
 * Order Form — Horizon Theme 3.4.0
 * assets/section-order-form.js
 *
 * Fetches products from /collections/{handle}/products.json and renders
 * them client-side. No Section Rendering API dependency.
 */

(function () {
  'use strict';

  // ── Utilities ─────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      const fmt = window.theme?.moneyFormat || window.Shopify.money_format || '£{{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function debounce(fn, ms) {
    let t;
    return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), ms); };
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── Image URL helper ──────────────────────────────────────────────────────
  //
  // Shopify's /products.json API returns images as:
  //   product.featured_image → a plain string URL like "//cdn.shopify.com/.../image.jpg"
  //   variant.featured_image → null, OR an object { id, product_id, src, ... }
  //   product.images[]       → array of objects with a .src string
  //
  // All CDN URLs may be protocol-relative (//cdn...) which browsers handle fine
  // in HTML but breaks when used in JS string comparisons, so we normalise.
  //
  // Shopify's CDN image resizing works by appending ?width=N to the URL.
  // If the URL already has a query string (e.g. ?v=1234) we must use & not ?.

  function getImageSrc(product, variant, px) {
    let raw = null;

    // Priority 1: variant-specific image object
    if (variant.featured_image && typeof variant.featured_image === 'object' && variant.featured_image.src) {
      raw = variant.featured_image.src;
    }

    // Priority 2: product.featured_image (string or object)
    if (!raw && product.featured_image) {
      raw = (typeof product.featured_image === 'object')
        ? product.featured_image.src
        : product.featured_image;
    }

    // Priority 3: first entry in product.images array
    if (!raw && Array.isArray(product.images) && product.images.length > 0) {
      const first = product.images[0];
      raw = (typeof first === 'object') ? first.src : first;
    }

    if (!raw) return null;

    // Normalise protocol-relative URLs
    const base = String(raw).replace(/^\/\//, 'https://');

    // Append Shopify CDN width param correctly
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}width=${px}`;
  }

  // ── Row HTML builder ──────────────────────────────────────────────────────

  function buildProductRow(product, variant, settings) {
    const imagePx     = { small: 64, medium: 80, large: 100 }[settings.imageSize] || 80;
    const imageSrc    = getImageSrc(product, variant, imagePx);
    const available   = variant.available !== false;
    const disabled    = available ? '' : 'disabled';

    const imageHtml = imageSrc
      ? `<a href="/products/${product.handle}" tabindex="-1" aria-hidden="true">
           <img
             class="order-form__product-image"
             src="${imageSrc}"
             alt="${escHtml(product.title)}"
             loading="lazy"
             width="${imagePx}"
             height="${imagePx}"
           >
         </a>`
      : `<div class="order-form__product-image order-form__product-image--placeholder"></div>`;

    const variantTitle = (variant.title && variant.title !== 'Default Title')
      ? `<span class="order-form__variant-title"> — ${escHtml(variant.title)}</span>`
      : '';

    const vendorHtml = (settings.showVendor && product.vendor)
      ? `<p class="order-form__product-vendor">${escHtml(product.vendor)}</p>`
      : '';

    const skuHtml = (settings.showSku && variant.sku)
      ? `<p class="order-form__product-sku">${escHtml(variant.sku)}</p>`
      : '';

    const priceHtml = settings.showPrice
      ? `<p class="order-form__product-price">${
          (variant.compare_at_price && variant.compare_at_price > variant.price)
            ? `<s class="order-form__price--compare">${formatMoney(variant.compare_at_price)}</s> `
            : ''
        }${formatMoney(variant.price)}</p>`
      : '';

    const soldOutHtml = !available
      ? `<span class="order-form__unavailable-badge badge badge--sold-out">Sold out</span>`
      : '';

    return `
      <div
        class="order-form__product-row"
        data-product-row
        data-variant-id="${variant.id}"
        data-variant-price="${variant.price}"
        data-available="${available}"
      >
        <p class="order-form__product-title">${escHtml(product.title)}${variantTitle}</p>
        <div class="order-form__product-inner">
          <div class="order-form__product-image-wrap order-form__product-image-wrap--${settings.imageSize}">
            ${imageHtml}
          </div>
          <div class="order-form__product-details">
            ${vendorHtml}${skuHtml}${priceHtml}${soldOutHtml}
            <div class="order-form__quantity" data-quantity-wrapper>
              <button
                type="button"
                class="order-form__qty-btn order-form__qty-btn--minus"
                data-qty-minus
                aria-label="Decrease quantity: ${escHtml(product.title)}"
                ${disabled}
              ><span aria-hidden="true">&#8722;</span></button>
              <input
                type="number"
                class="order-form__qty-input"
                data-qty-input
                value="0"
                min="0"
                max="9999"
                step="1"
                aria-label="Quantity: ${escHtml(product.title)}"
                ${disabled}
              >
              <button
                type="button"
                class="order-form__qty-btn order-form__qty-btn--plus"
                data-qty-plus
                aria-label="Increase quantity: ${escHtml(product.title)}"
                ${disabled}
              ><span aria-hidden="true">&#43;</span></button>
            </div>
          </div>
        </div>
      </div>`;
  }

  // ── Collection product fetching ───────────────────────────────────────────
  // Handles pagination — Shopify's products.json maxes at 250 per page.

  async function fetchCollectionProducts(handle, limit, signal) {
    const products = [];
    let page = 1;
    const perPage = Math.min(limit, 250);

    while (products.length < limit) {
      const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${perPage}&page=${page}`;
      const res = await fetch(url, {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} fetching "${handle}"`);

      const data  = await res.json();
      const batch = data.products || [];
      products.push(...batch);

      if (batch.length < perPage) break; // no more pages
      page++;
    }

    return products.slice(0, limit);
  }

  // ── Variant matching ──────────────────────────────────────────────────────

  function findVariant(product, filterKw) {
    if (!product.variants || product.variants.length === 0) return null;
    if (!filterKw) return product.variants[0];
    const kw = filterKw.toLowerCase();
    return product.variants.find(v => v.title && v.title.toLowerCase().includes(kw)) || null;
  }

  // ── Toast ─────────────────────────────────────────────────────────────────

  let _toast;
  function showToast(msg, type) {
    if (!_toast) {
      _toast = document.createElement('div');
      _toast.className = 'order-form__toast';
      document.body.appendChild(_toast);
    }
    _toast.textContent = msg;
    _toast.className   = `order-form__toast is-${type || 'info'}`;
    clearTimeout(_toast._t);
    void _toast.offsetWidth;
    _toast.classList.add('is-visible');
    _toast._t = setTimeout(() => _toast.classList.remove('is-visible'), 3500);
  }

  // ── OrderForm class ───────────────────────────────────────────────────────

  class OrderForm {
    constructor(el) {
      this.el        = el;
      this.sectionId = el.dataset.sectionId;

      // Read embedded JSON config
      const cfgEl = document.getElementById(`order-form-config-${this.sectionId}`);
      this.config = cfgEl ? JSON.parse(cfgEl.textContent) : null;

      if (!this.config || !this.config.collections || !this.config.collections.length) {
        console.warn('[OrderForm] No config found for section', this.sectionId);
        return;
      }

      // Fast lookup map
      this.collectionMap = {};
      this.config.collections.forEach(c => { this.collectionMap[c.blockId] = c; });

      this.select    = el.querySelector('[data-order-form-select]');
      this.body      = el.querySelector('[data-order-form-body]');
      this.cartBar   = el.querySelector('[data-order-form-cart-bar]');
      this.submitBtn = el.querySelector('[data-order-form-submit]');
      this.countEl   = el.querySelector('[data-order-form-item-count]');
      this.totalEl   = el.querySelector('[data-order-form-total]');

      // Map<variantId, { qty, price }>
      this.cart = new Map();

      // Cache fetched products so switching back is instant
      this.cache = new Map();

      this._abort = null;

      this._init();
    }

    _init() {
      this._bindSelect();
      this._bindBody();
      this._bindSubmit();

      // Load the initial collection — prefer the select's current value,
      // fall back to the first configured collection
      const initialId = (this.select && this.select.value)
        || (this.config.collections[0] && this.config.collections[0].blockId);

      if (initialId) this._loadCollection(initialId);
    }

    // ── Collection switching ────────────────────────────────────────────────

    _bindSelect() {
      if (!this.select) return;
      this.select.addEventListener('change', () => this._loadCollection(this.select.value));
    }

    async _loadCollection(blockId) {
      const coll = this.collectionMap[blockId];
      if (!coll) { console.error('[OrderForm] Unknown blockId:', blockId); return; }

      if (this._abort) this._abort.abort();
      this._abort = new AbortController();
      this._setLoading(true);

      try {
        let products = this.cache.get(coll.handle);

        if (!products) {
          products = await fetchCollectionProducts(
            coll.handle, this.config.productsLimit, this._abort.signal
          );
          this.cache.set(coll.handle, products);
        }

        this._renderGrid(products, coll);

      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[OrderForm] Load failed:', err);
        this._renderError(window.orderFormStrings?.loadError || 'Could not load products. Please try again.');
      } finally {
        this._setLoading(false);
      }
    }

    _setLoading(on) {
      if (!this.body) return;
      if (on) {
        this.body.classList.add('is-loading');
        this.body.innerHTML = '<div class="order-form__loading-spinner" aria-hidden="true"></div>';
        if (this.select) this.select.disabled = true;
      } else {
        this.body.classList.remove('is-loading');
        if (this.select) this.select.disabled = false;
      }
    }

    _renderGrid(products, coll) {
      if (!this.body) return;

      const settings = {
        imageSize:  this.config.imageSize  || 'medium',
        showVendor: !!this.config.showVendor,
        showSku:    this.config.showSku    !== false,
        showPrice:  this.config.showPrice  !== false,
      };

      const rows = [];
      for (const product of products) {
        const variant = findVariant(product, coll.variantFilter);
        if (!variant) continue;
        rows.push(buildProductRow(product, variant, settings));
      }

      if (!rows.length) {
        this.body.innerHTML = `<p class="order-form__empty-state">${
          escHtml(window.orderFormStrings?.noProducts || 'No products match the selected filter.')
        }</p>`;
        return;
      }

      this.body.innerHTML = `<div class="order-form__product-grid" data-collection-panel="${escHtml(coll.blockId)}">${rows.join('')}</div>`;
      this._reapplyQuantities();
    }

    _renderError(msg) {
      if (this.body) this.body.innerHTML = `<p class="order-form__empty-state">${escHtml(msg)}</p>`;
    }

    _reapplyQuantities() {
      this.body.querySelectorAll('[data-product-row]').forEach(row => {
        const entry = this.cart.get(row.dataset.variantId);
        if (entry && entry.qty > 0) {
          const input = row.querySelector('[data-qty-input]');
          if (input) input.value = entry.qty;
          row.dataset.hasQuantity = 'true';
        }
      });
    }

    // ── Quantity controls ───────────────────────────────────────────────────

    _bindBody() {
      if (!this.body) return;

      this.body.addEventListener('click', e => {
        const minus = e.target.closest('[data-qty-minus]');
        const plus  = e.target.closest('[data-qty-plus]');
        if (minus) this._step(minus.closest('[data-product-row]'), -1);
        if (plus)  this._step(plus.closest('[data-product-row]'),  +1);
      });

      this.body.addEventListener('input', debounce(e => {
        const input = e.target.closest('[data-qty-input]');
        if (input) this._commitInput(input.closest('[data-product-row]'), input);
      }, 200));

      this.body.addEventListener('keydown', e => {
        const input = e.target.closest('[data-qty-input]');
        if (!input) return;
        const nav = ['Backspace','Delete','Tab','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown'];
        if (!/^\d$/.test(e.key) && !nav.includes(e.key)) e.preventDefault();
      });
    }

    _step(row, delta) {
      if (!row) return;
      const input = row.querySelector('[data-qty-input]');
      if (!input) return;
      const next = Math.min(9999, Math.max(0, (parseInt(input.value, 10) || 0) + delta));
      input.value = next;
      this._onChange(row, next);
    }

    _commitInput(row, input) {
      if (!row || !input) return;
      let v = parseInt(input.value, 10);
      if (isNaN(v)) v = 0;
      v = Math.min(9999, Math.max(0, v));
      input.value = v;
      this._onChange(row, v);
    }

    _onChange(row, qty) {
      const vid   = row.dataset.variantId;
      const price = parseInt(row.dataset.variantPrice, 10) || 0;
      if (!vid) return;

      const entry = this.cart.get(vid) || { qty: 0, price };
      entry.qty   = qty;
      entry.price = price;
      this.cart.set(vid, entry);

      if (qty > 0) row.dataset.hasQuantity = 'true';
      else delete row.dataset.hasQuantity;

      this._updateCartBar();
    }

    // ── Cart bar ────────────────────────────────────────────────────────────

    _updateCartBar() {
      let totalQty = 0, totalPrice = 0;
      this.cart.forEach(({ qty, price }) => { totalQty += qty; totalPrice += qty * price; });

      if (this.countEl) this.countEl.textContent = totalQty;
      if (this.totalEl) this.totalEl.textContent  = formatMoney(totalPrice);

      this.cartBar?.classList.toggle('is-visible', totalQty > 0);
      if (this.submitBtn) this.submitBtn.disabled = totalQty === 0;
    }

    // ── Add to cart ─────────────────────────────────────────────────────────

    _bindSubmit() {
      this.submitBtn?.addEventListener('click', () => this._submit());
    }

    async _submit() {
      const items = [];
      this.cart.forEach(({ qty }, vid) => {
        if (qty > 0) items.push({ id: parseInt(vid, 10), quantity: qty });
      });
      if (!items.length) return;

      this.submitBtn.classList.add('is-loading');
      this.submitBtn.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `HTTP ${res.status}`);
        }

        await res.json();

        // Reset
        this.cart.forEach(entry => { entry.qty = 0; });
        this.body.querySelectorAll('[data-qty-input]').forEach(i => { i.value = 0; });
        this.body.querySelectorAll('[data-product-row]').forEach(r => { delete r.dataset.hasQuantity; });
        this._updateCartBar();

        showToast(window.orderFormStrings?.addedToCart || 'Added to cart!', 'success');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        this._refreshCartBubble();

      } catch (err) {
        console.error('[OrderForm] Cart error:', err);
        showToast(err.message || (window.orderFormStrings?.error || 'Something went wrong.'), 'error');
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.removeAttribute('aria-busy');
      }
    }

    async _refreshCartBubble() {
      try {
        const res  = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const cart = await res.json();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
      } catch (_) { /* silent */ }
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[data-section-type="order-form"]').forEach(el => {
      if (!el._orderForm) el._orderForm = new OrderForm(el);
    });
  }

  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector?.('[data-section-type="order-form"]') || e.target;
    if (el?.dataset?.sectionType === 'order-form' && !el._orderForm) el._orderForm = new OrderForm(el);
  });

  document.addEventListener('shopify:section:unload', e => {
    const el = e.target.querySelector?.('[data-section-type="order-form"]') || e.target;
    if (el?._orderForm) delete el._orderForm;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
